import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { cn } from '@/lib/cn'

/**
 * Client-side QR render (works offline in PWA).
 */
export function QrCode({
  value,
  size = 180,
  className,
  label,
}: {
  value: string
  size?: number
  className?: string
  label?: string
}) {
  const [src, setSrc] = useState<string | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    setError(false)
    void QRCode.toDataURL(value, {
      width: size * 2,
      margin: 1,
      errorCorrectionLevel: 'M',
      color: {
        dark: '#0b0e11',
        light: '#ffffff',
      },
    })
      .then((url) => {
        if (!cancelled) setSrc(url)
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })
    return () => {
      cancelled = true
    }
  }, [value, size])

  return (
    <div
      className={cn(
        'relative inline-flex flex-col items-center gap-2',
        className,
      )}
    >
      <div
        className={cn(
          'relative rounded-xl bg-white p-3 shadow-sm',
          'ring-1 ring-terminal-border/50',
        )}
        style={{ width: size + 24, height: size + 24 }}
      >
        {/* Corner marks */}
        <span className="pointer-events-none absolute start-1.5 top-1.5 h-3 w-3 rounded-tl border-s-2 border-t-2 border-accent" />
        <span className="pointer-events-none absolute end-1.5 top-1.5 h-3 w-3 rounded-tr border-e-2 border-t-2 border-accent" />
        <span className="pointer-events-none absolute bottom-1.5 start-1.5 h-3 w-3 rounded-bl border-s-2 border-b-2 border-accent" />
        <span className="pointer-events-none absolute bottom-1.5 end-1.5 h-3 w-3 rounded-br border-e-2 border-b-2 border-accent" />

        {src && !error ? (
          <img
            src={src}
            alt={label ?? 'QR code'}
            width={size}
            height={size}
            className="h-full w-full rounded-md"
            draggable={false}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-terminal-elevated text-[10px] text-terminal-muted">
            …
          </div>
        )}
      </div>
    </div>
  )
}
